import { Currency, ExchangeRates } from '../context/CurrencyContext';

export interface GeolocationData {
  country_code: string;
  country_name: string;
  currency: string;
}

export interface FirebaseExchangeRateResponse {
  rates: ExchangeRates;
  lastUpdated: string;
  source: string;
}

class CurrencyService {
  private readonly apiBaseUrl: string;
  private ratesCache: FirebaseExchangeRateResponse | null = null;
  private cacheExpiry: number = 0;
  private readonly cacheTimeout = 15 * 60 * 1000; // 15 minutes

  constructor() {
    this.apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001';
  }

  /**
   * Fetch exchange rates from Firebase Functions
   */
  async getExchangeRates(): Promise<ExchangeRates> {
    // Return cached rates if still valid
    if (this.ratesCache && Date.now() < this.cacheExpiry) {
      return this.ratesCache.rates;
    }

    try {
      const response = await fetch(`${this.apiBaseUrl}/exchange-rates`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch exchange rates: ${response.status}`);
      }

      const data: FirebaseExchangeRateResponse = await response.json();
      
      // Validate the response structure
      if (!data.rates || typeof data.rates !== 'object') {
        throw new Error('Invalid exchange rates response format');
      }

      // Cache the response
      this.ratesCache = data;
      this.cacheExpiry = Date.now() + this.cacheTimeout;

      return data.rates;
    } catch (error) {
      console.error('Error fetching exchange rates:', error);
      
      // Return default rates if fetch fails
      return {
        USD: 1,
        CAD: 1.35,
        GBP: 0.79
      };
    }
  }

  /**
   * Detect user's location based on IP address
   */
  async detectUserLocation(): Promise<Currency> {
    try {
      const response = await fetch('https://ipapi.co/json/', {
        method: 'GET',
        headers: {
          'User-Agent': 'JamPackd-Currency-Detection/1.0'
        }
      });

      if (!response.ok) {
        throw new Error(`Geolocation service failed: ${response.status}`);
      }

      const data: GeolocationData = await response.json();
      
      return this.mapCountryToCurrency(data.country_code);
    } catch (error) {
      console.warn('Failed to detect user location:', error);
      return 'USD'; // Default fallback
    }
  }

  /**
   * Map country code to supported currency
   */
  private mapCountryToCurrency(countryCode: string): Currency {
    switch (countryCode?.toUpperCase()) {
      case 'CA':
        return 'CAD';
      case 'GB':
      case 'UK':
        return 'GBP';
      default:
        return 'USD';
    }
  }

  /**
   * Get currency symbol for display
   */
  getCurrencySymbol(currency: Currency): string {
    const symbols: Record<Currency, string> = {
      USD: '$',
      CAD: '$',
      GBP: '£'
    };
    return symbols[currency];
  }

  /**
   * Get currency name for display
   */
  getCurrencyName(currency: Currency): string {
    const names: Record<Currency, string> = {
      USD: 'US Dollar',
      CAD: 'Canadian Dollar',
      GBP: 'British Pound'
    };
    return names[currency];
  }

  /**
   * Convert price from one currency to another
   */
  convertPrice(
    amount: number, 
    fromCurrency: Currency, 
    toCurrency: Currency, 
    rates: ExchangeRates
  ): number {
    if (fromCurrency === toCurrency) {
      return amount;
    }

    // Convert to USD first if source is not USD
    const usdAmount = fromCurrency === 'USD' ? amount : amount / rates[fromCurrency];
    
    // Then convert from USD to target currency
    return toCurrency === 'USD' ? usdAmount : usdAmount * rates[toCurrency];
  }

  /**
   * Format price with currency symbol
   */
  formatPrice(amount: number, currency: Currency): string {
    const symbol = this.getCurrencySymbol(currency);
    // Only round up to ceiling dollar for non-USD currencies
    const finalAmount = currency === 'USD' ? amount : Math.ceil(amount);
    return `${symbol}${finalAmount.toFixed(2)}`;
  }

  /**
   * Clear cached exchange rates (useful for testing)
   */
  clearCache(): void {
    this.ratesCache = null;
    this.cacheExpiry = 0;
  }
}

// Export singleton instance
export const currencyService = new CurrencyService();
export default currencyService;